/** Conteúdos alinhados ao curso "Fundamentos de Capacitação Corporativa". */

export const oQueECulturaAprendizagemMarkdown = `
# O que é cultura de aprendizagem

Cultura de aprendizagem é a capacidade de uma pessoa ou empresa de **aprender e se adaptar** às mudanças do negócio, da tecnologia e do mercado.

Não se trata apenas de oferecer treinamentos. Trata-se de tornar o desenvolvimento parte da rotina: aprender, aplicar, revisar e aprender de novo.

## Por que isso importa

Em um cenário de transformação constante, a ausência de educação corporativa aumenta o risco de obsoletar processos, produtos e competências. Já a cultura de aprendizagem:

- conecta desenvolvimento das pessoas aos objetivos estratégicos;
- reduz a dependência de ações pontuais e isoladas;
- fortalece engajamento, retenção e performance.

## Cultura de aprendizagem x treinamento isolado

| Treinamento isolado | Cultura de aprendizagem |
| --- | --- |
| Evento pontual | Processo contínuo |
| Foco em presença | Foco em aplicação e impacto |
| Conteúdo genérico | Trilhas alinhadas a competências |
| Responsabilidade só do RH | Liderança e times envolvidos |

## Em resumo

Cultura de aprendizagem é estratégia de negócio: quem aprende mais rápido se adapta melhor.
`;

export const capacitacaoPontualParaContinuaMarkdown = `
# Da capacitação pontual à aprendizagem contínua

A educação corporativa moderna vai além da “grade de cursos”. Ela cria um **sistema contínuo** de desenvolvimento, alinhado à cultura e aos resultados da organização.

## O que muda na prática

1. **Diagnóstico** das competências críticas para o negócio
2. **Trilhas com propósito**, em vez de conteúdos soltos
3. **Aplicação no trabalho**, com apoio da liderança
4. **Mensuração de impacto**, não só de conclusão

## Aprendizagem contínua (lifelong learning)

Competências técnicas e comportamentais envelhecem rápido. Por isso, o ciclo deixa de ser anual e passa a acontecer em fluxo: aprender no momento da necessidade, praticar e ajustar.

> Trilha de aprendizagem não é o mesmo que lista de cursos: a trilha organiza uma sequência com objetivo claro de competência.

## Como começar

- Defina quais competências realmente impactam o resultado.
- Estruture jornadas curtas e progressivas.
- Incentive líderes a aprenderem junto com o time.
- Revise conteúdos com base em dados de uso e desempenho.
`;

export const trilhasMicrolearningEngajamentoMarkdown = `
# Trilhas, microlearning e engajamento

## Trilhas de aprendizagem

A trilha combina as necessidades da empresa com os objetivos do colaborador. Ela organiza conhecimentos teóricos e práticos em uma jornada — com autonomia e propósito.

Diferente de uma grade (o que está disponível), a trilha define **sequência e competência-alvo**.

## Microlearning

São pílulas curtas: vídeos, leituras, quizzes e desafios rápidos. Cabem na rotina e funcionam bem quando combinados com prática imediata.

## Engajamento na capacitação

Para aumentar participação:

- comunique o “porquê” do programa;
- use formatos variados (vídeo, texto, prática, mentoria);
- reconheça progresso e aplicação no trabalho;
- evite sobrecarga de conteúdo sem contexto.

## Gamificação com propósito

Rankings e recompensas ajudam quando estão ligados a objetivos reais de desenvolvimento — não apenas à competição vazia.
`;

export const praticasEngajamentoCapacitacaoMarkdown = `
# Práticas de engajamento em capacitação

Engajar equipes em programas de capacitação exige experiência útil, acessível e reconhecida.

## Práticas recomendadas

1. **Onboarding com trilha clara** — primeiros passos com objetivo e prazo.
2. **Conteúdo no fluxo de trabalho** — recursos disponíveis no momento da necessidade.
3. **Microlearning + prática** — aprender pouco, aplicar rápido.
4. **Comunidades e mentorias** — aprendizagem social e troca de experiências.
5. **Reconhecimento** — celebrar quem aplica o aprendizado no dia a dia.
6. **Feedback contínuo** — ajustar a jornada com base na experiência do colaborador.

## Tecnologia a favor

Plataformas de aprendizagem (LMS/LXP) centralizam trilhas, acompanham engajamento e permitem personalizar jornadas com dados.

## Checklist do gestor

- O time entende o benefício para a carreira?
- Há tempo protegido para aprender?
- O conteúdo está conectado aos desafios reais da área?
- Há indicadores simples de participação e aplicação?
`;

export const medirImpactoCapacitacaoMarkdown = `
# Como medir o impacto da capacitação

Capacitação gera valor quando há **evidência de impacto**. Conclusão de curso é só o começo.

## Três camadas de indicadores

### 1. Aprendizagem
- Conclusão de trilhas e avaliações
- Tempo médio de progresso
- Taxa de aprovação

### 2. Comportamento
- Aplicação do conteúdo na rotina
- Qualidade das entregas após o programa
- Feedback da liderança sobre mudança de prática

### 3. Negócio
- Produtividade e redução de erros/retrabalho
- Retenção de talentos
- Clima e engajamento (itens de desenvolvimento)
- Evolução de competências críticas

## Como reportar para a liderança

Prefira narrativas com causa e efeito:

> “Após a trilha X, o time Y reduziu retrabalho em Z%.”

Isso é mais útil do que apenas “90% concluíram o curso”.
`;

export const painelIndicadoresCapacitacaoMarkdown = `
# Painel de indicadores de desempenho em capacitação

Um painel de L&D (Learning & Development) ajuda a acompanhar se a estratégia de educação corporativa está funcionando.

## Visão sugerida do painel

- **Participação:** ativos na trilha, conclusões no período
- **Aprendizagem:** média de avaliações, taxa de aprovação
- **Engajamento:** frequência de acesso, conclusão no prazo
- **Impacto:** indicadores de área (qualidade, produtividade, clima)

## Boas práticas

- Escolha poucos indicadores, mas relevantes ao negócio.
- Compare períodos (antes/depois da trilha).
- Cruze dados de aprendizagem com dados de performance.
- Revise metas quando o contexto do negócio mudar.

## Evite armadilhas

- Medir só presença ou conclusão
- Ter muitos KPIs sem decisão associada
- Não envolver líderes na leitura dos resultados
`;
